import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { GENERAL_SUBJECT, MAX_RULES_PER_CATEGORY, MEMORY_FIELDS } from '../../shared/memory.js';
import { isSubject } from '../subjects.js';
import { Teacher } from '../teacher.js';

/** A rule's subject: a canonical subject key, or the 'general' sentinel when unknown/cross-subject. */
const normalizeSubject = (value: unknown): string => (isSubject(value) ? (value as string) : GENERAL_SUBJECT);

/** Plain document shape stored for each rule (matches the MemoryRule entity). */
interface StoredRule {
  _class: 'MemoryRule';
  rule: string;
  subject: string;
}

/**
 * Sanitize one category's rules coming from the profile editor: keep only non-empty single-line
 * rule texts, coerce the subject to a valid key or 'general', and enforce the per-category cap.
 * Unlike the agent's save path this stores the teacher's text verbatim (no LLM curation).
 */
const sanitizeRules = (value: unknown): StoredRule[] => {
  if (!Array.isArray(value)) return [];
  const rules = value
    .map((item): StoredRule | null => {
      const rule = String((item as any)?.rule ?? '')
        .replace(/\s*\n+\s*/g, ' ')
        .trim();
      return rule ? { _class: 'MemoryRule', rule, subject: normalizeSubject((item as any)?.subject) } : null;
    })
    .filter((r): r is StoredRule => r !== null);
  return rules.slice(0, MAX_RULES_PER_CATEGORY);
};

/**
 * Persist the teacher's manually edited preference rules from the profile tab. Accepts the full
 * memory as a JSON string ({ chattingStyle: [{rule, subject}], ... }), sanitizes and caps each
 * category, and writes the rebuilt Memory document back. Only the logged-in teacher's own memory is
 * affected. Returns true when the teacher document was matched.
 */
export const editTeacherMemoryMutation = registerMutation({
  operationName: 'editTeacherMemoryMutation',
  type: () => Boolean,
  params: [{ name: 'memoryJson', type: () => String }],
  resolve: (memoryJson: string) => async () => {
    const sessionData = await CurrentUserInfo.getCurrentUserInfo()({ userID: 1 });
    if (!sessionData?.userID) {
      throw new Error('Nicht autorisiert: Kein User eingeloggt');
    }

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(memoryJson || '{}') as Record<string, unknown>;
    } catch {
      throw new Error('Ungültiges Memory-Format');
    }

    const teacherCollection = await getCollection(Teacher);
    const existing = await teacherCollection.findOne({ userID: sessionData.userID });
    const baseMemory = (existing?.memory ?? {}) as Record<string, unknown>;

    const newMemory: Record<string, unknown> = {
      _class: 'Memory',
      ...(baseMemory._id ? { _id: baseMemory._id } : {}),
    };
    for (const field of MEMORY_FIELDS) {
      newMemory[field] = sanitizeRules(parsed[field]);
    }

    const result = await teacherCollection.updateOne(
      { userID: sessionData.userID },
      { $set: { memory: newMemory } } as any,
    );

    return result.matchedCount > 0;
  },
});
